import type {
  Money,
  AvailableLimitRevenueRate,
  Agreements,
  MarketingAgreementType,
} from '@repo/shared/types';
import type { MerchantDataPayload } from '@repo/api/endpoints/merchant';
import { agreementsNames, marketingAgreementType, ProviderType } from '@repo/shared/types';
import { SPECIAL_OFFER_LOAN_AMOUNT } from '@repo/shared/constants';
import { env } from '@repo/env';
import { getDefaultPeriod } from '@repo/shared/helpers';
import {
  getMerchantIbanData,
  getMerchantRepresentativeId,
  getP24MarketingAgreements,
} from '@repo/store/helpers';

import { isAgreementFieldVisible } from '@/components/loan-form/loan-agreements/agreements-helper';
import { shouldPreselectPromotionTenor } from '@/components/loan-form/helpers/should-preselect-promotion-tenor';

// Pre-fill a P24 marketing channel (email/sms/tel) from existing accepted agreements,
// mirroring the welcome flow's default values.
const isP24MarketingChannelAccepted = (
  marketing: Agreements['marketing'],
  type: MarketingAgreementType,
) =>
  marketing?.find((agreement) => 'type' in agreement && agreement.type === type)?.accepted ?? false;

const setAgreementsInitialData = (data: MerchantDataPayload) => {
  return Object.fromEntries(
    agreementsNames.options.map((name) => {
      const visible = isAgreementFieldVisible(name, data);

      if (visible && name === agreementsNames.enum.marketing) {
        return [name, false];
      }

      return [name, visible ? false : undefined];
    }),
  );
};

export const commonLoanFormInitialData = ({
  data,
  provider,
}: {
  data: MerchantDataPayload;
  provider: ProviderType;
}) => {
  const p24marketingAgreements = getP24MarketingAgreements(data.agreements?.marketing);

  return {
    bankAccount: getMerchantIbanData(data.merchantData),
    pesel: data.merchantData.representative[0]?.pesel,
    documentId: data.merchantData.representative[0]?.document.number,
    documentType: data.merchantData.representative[0]?.document.type,
    taxId: data.merchantData.vat,
    mail: data.merchantData.email,
    phone: data.merchantData.phone,
    // eslint-disable-next-line camelcase
    name_surname: data.merchantData.representative[0]
      ? getMerchantRepresentativeId(data.merchantData)
      : undefined,
    ...setAgreementsInitialData(data),
    companyName: data.merchantData.name,
    provider: provider,
    // P24 marketing agreements (email/sms/tel) — pre-filled from existing accepted
    // agreements (boolean keeps the Checkbox controlled so "select all" reflects in the UI).
    email: isP24MarketingChannelAccepted(p24marketingAgreements, marketingAgreementType.enum.email),
    sms: isP24MarketingChannelAccepted(p24marketingAgreements, marketingAgreementType.enum.sms),
    tel: isP24MarketingChannelAccepted(p24marketingAgreements, marketingAgreementType.enum.tel),
  };
};

// Zwraca pierwszy okres, którego zakres [amountMin, amountMax] obejmuje podaną kwotę.
// Okresy bez pełnego zakresu (amountMin/amountMax === null) są pomijane.
const findPeriodForAmount = (rates: AvailableLimitRevenueRate, amount: number) =>
  rates.find(
    (rate) =>
      rate.amountMin != null &&
      rate.amountMax != null &&
      amount >= rate.amountMin &&
      amount <= rate.amountMax,
  );

export const loanFormInitialData = ({
  data,
  amount,
  availableLimitRevenueRate,
  isSpecialOffer,
  provider,
  widgetInitialAmount,
}: {
  data: MerchantDataPayload;
  amount: Money;
  availableLimitRevenueRate: AvailableLimitRevenueRate;
  provider: ProviderType;
  isSpecialOffer: boolean;
  // Kwota wybrana na sliderze widgetu (handoff przez cookie). Kwota i okres są zależne:
  // jeśli podana i mieści się w zakresie któregoś okresu, wybieramy ten okres i tę kwotę.
  widgetInitialAmount?: number;
}) => {
  const widgetPeriodRate =
    widgetInitialAmount !== undefined
      ? findPeriodForAmount(availableLimitRevenueRate, widgetInitialAmount)
      : undefined;

  const preselectPromotionTenor = shouldPreselectPromotionTenor(isSpecialOffer);

  const getPeriod = () => {
    if (widgetPeriodRate) {
      return widgetPeriodRate.period.toString();
    }
    if (preselectPromotionTenor && env.VITE_PRAGMA_PROMOTION_TENOR) {
      return env.VITE_PRAGMA_PROMOTION_TENOR.toString();
    }

    return getDefaultPeriod(availableLimitRevenueRate);
  };

  const period = getPeriod();

  const getInitialMoney = () =>
    !period || provider !== 'pragma'
      ? amount
      : (availableLimitRevenueRate.find((rate) => rate.period === parseInt(period))?.amountMax ??
        amount);

  const getInitialAmount = () => {
    if (widgetInitialAmount !== undefined && widgetPeriodRate) {
      return widgetInitialAmount;
    }
    if (preselectPromotionTenor) {
      return SPECIAL_OFFER_LOAN_AMOUNT;
    }

    return getInitialMoney();
  };

  const initialAmount = getInitialAmount();

  return {
    ...commonLoanFormInitialData({ data, provider }),
    amount: initialAmount,
    period,
  };
};
